import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, Fragment, useEffect } from "react";
import { ArrowLeft, CheckCircle2, XCircle, Clock, Loader2, Play, X, RotateCw, ChevronDown, ChevronRight, Timer } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  listPushQueue,
  pushNowQueueItem,
  cancelQueueItem,
  retryFailedPush,
  listWorkspaceSettleDelays,
  updateWorkspaceSettleDelay,
  flushWorkspacePushQueue,
} from "@/lib/integrations.functions";
import { Input } from "@/components/ui/input";

export const Route = createFileRoute("/_authenticated/settings/integrations/push-management")({
  component: PushManagementPage,
});

type StatusFilter = "pending" | "processing" | "succeeded" | "failed" | "all";

function PushManagementPage() {
  const [tab, setTab] = useState<StatusFilter>("succeeded");
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const toggle = (id: string) => setExpanded((p) => ({ ...p, [id]: !p[id] }));
  const fetchList = useServerFn(listPushQueue);
  const pushNowFn = useServerFn(pushNowQueueItem);
  const cancelFn = useServerFn(cancelQueueItem);
  const retryFn = useServerFn(retryFailedPush);
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["push-queue", tab],
    queryFn: () => fetchList({ data: { status: tab, limit: 200 } }),
    refetchInterval: 15_000,
  });

  const totals = data?.totals ?? { pending: 0, processing: 0, succeeded: 0, failed: 0, cancelled: 0, skipped: 0 };
  const items = data?.items ?? [];

  const invalidate = () => qc.invalidateQueries({ queryKey: ["push-queue"] });

  const pushNow = useMutation({
    mutationFn: (id: string) => pushNowFn({ data: { id } }),
    onSuccess: () => { toast.success("Pushed to front of queue"); invalidate(); },
    onError: (e: any) => toast.error(e.message),
  });
  const cancel = useMutation({
    mutationFn: (id: string) => cancelFn({ data: { id } }),
    onSuccess: () => { toast.success("Push cancelled"); invalidate(); },
    onError: (e: any) => toast.error(e.message),
  });
  const retry = useMutation({
    mutationFn: (id: string) => retryFn({ data: { id } }),
    onSuccess: () => { toast.success("Retry queued"); invalidate(); },
    onError: (e: any) => toast.error(e.message),
  });

  return (
    <div className="space-y-6">
      <div>
        <Link to="/settings/integrations" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
          <ArrowLeft className="h-3 w-3" /> Back to integrations
        </Link>
        <h1 className="text-2xl font-semibold mt-2">Push history</h1>
        <p className="text-sm text-muted-foreground">Every contact pushed to an integration in the last 7 days. Click any row to inspect the exact payload that was sent and the lifecycle event that triggered it.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={<Clock className="h-4 w-4 text-amber-600" />} label="Pending" value={totals.pending + totals.processing} />
        <StatCard icon={<CheckCircle2 className="h-4 w-4 text-green-600" />} label="Succeeded (7d)" value={totals.succeeded} />
        <StatCard icon={<XCircle className="h-4 w-4 text-red-600" />} label="Failed (7d)" value={totals.failed} />
        <StatCard icon={<X className="h-4 w-4 text-muted-foreground" />} label="Cancelled (7d)" value={totals.cancelled} />
      </div>

      <SettleDelayCard />


      <Tabs value={tab} onValueChange={(v) => setTab(v as StatusFilter)}>
        <TabsList>
          <TabsTrigger value="succeeded">Succeeded</TabsTrigger>
          <TabsTrigger value="failed">Failed</TabsTrigger>
          <TabsTrigger value="pending">Pending</TabsTrigger>
          <TabsTrigger value="processing">Processing</TabsTrigger>
          <TabsTrigger value="all">All</TabsTrigger>
        </TabsList>
        <TabsContent value={tab} className="mt-4">
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">{labelFor(tab)}</CardTitle></CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="flex items-center gap-2 text-muted-foreground py-8 justify-center"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
              ) : items.length === 0 ? (
                <div className="py-12 text-center text-sm text-muted-foreground">Nothing here.</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-6"></TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Contact</TableHead>
                      <TableHead>Action</TableHead>
                      <TableHead>Integration</TableHead>
                      <TableHead>Workspace</TableHead>
                      <TableHead>Attempts</TableHead>
                      <TableHead>When</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map((it: any) => {
                      const isOpen = !!expanded[it.id];
                      const summary = payloadSummary(it);
                      return (
                        <Fragment key={it.id}>
                          <TableRow className="cursor-pointer hover:bg-muted/40" onClick={() => toggle(it.id)}>
                            <TableCell className="pr-0">{isOpen ? <ChevronDown className="h-3 w-3 text-muted-foreground" /> : <ChevronRight className="h-3 w-3 text-muted-foreground" />}</TableCell>
                            <TableCell><StatusBadge status={it.status} /></TableCell>
                            <TableCell className="font-mono text-xs">{it.contact?.email ?? it.payload?.email ?? "—"}</TableCell>
                            <TableCell className="text-xs">{opLabel(it.operation)}{it.push_type === "manual" && <Badge variant="outline" className="ml-2 text-[10px]">manual</Badge>}{it.push_type === "test" && <Badge variant="outline" className="ml-2 text-[10px]">test</Badge>}{it.push_type === "state_transition" && <Badge variant="outline" className="ml-2 text-[10px]">automation</Badge>}</TableCell>
                            <TableCell className="text-xs">{it.integration?.display_name ?? "—"}</TableCell>
                            <TableCell className="text-xs">{it.workspace?.program_name ?? "—"}</TableCell>
                            <TableCell className="text-xs">{it.attempt_count}/{it.max_attempts}</TableCell>
                            <TableCell className="text-xs text-muted-foreground">
                              {(it.status === "pending" || it.status === "processing") && it.next_attempt_at ? (
                                <Countdown target={it.next_attempt_at} />
                              ) : (
                                timeAgo(it.succeeded_at ?? it.last_attempted_at ?? it.created_at)
                              )}
                              {it.status === "failed" && it.error_message && (
                                <div className="text-red-600 text-[10px] mt-0.5 max-w-[280px] truncate" title={it.error_message}>{it.error_message}</div>
                              )}
                            </TableCell>
                            <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                              <div className="flex justify-end gap-1">
                                {(it.status === "pending") && (
                                  <>
                                    <Button size="sm" variant="ghost" onClick={() => pushNow.mutate(it.id)} title="Push now"><Play className="h-3 w-3" /></Button>
                                    <Button size="sm" variant="ghost" onClick={() => cancel.mutate(it.id)} title="Cancel"><X className="h-3 w-3" /></Button>
                                  </>
                                )}
                                {it.status === "failed" && (
                                  <Button size="sm" variant="ghost" onClick={() => retry.mutate(it.id)} title="Retry"><RotateCw className="h-3 w-3" /></Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                          <TableRow className="border-0 hover:bg-transparent">
                            <TableCell colSpan={9} className="py-1 pl-10 text-[11px] text-muted-foreground">
                              <span className="font-medium text-foreground/70">Data:</span> {summary}
                            </TableCell>
                          </TableRow>
                          {isOpen && (
                            <TableRow className="border-0 bg-muted/30 hover:bg-muted/30">
                              <TableCell colSpan={9} className="pl-10 py-3">
                                <div className="space-y-2 text-xs">
                                  <DetailRow label="Trigger" value={triggerLabel(it)} />
                                  <DetailRow label="Why it fired" value={reasonLabel(it)} />
                                  <DetailRow label="Sent to" value={`${it.integration?.display_name ?? "—"} (${it.integration?.integration_type ?? "—"})`} />
                                  <DetailRow label="Operation" value={opLabel(it.operation)} />
                                  <DetailRow label="Contact" value={it.contact?.email ?? it.payload?.email ?? "—"} />
                                  {it.payload?.tag && <DetailRow label="Tag" value={String(it.payload.tag)} />}
                                  {it.payload?.tags && <DetailRow label="Tags" value={Array.isArray(it.payload.tags) ? it.payload.tags.join(", ") : String(it.payload.tags)} />}
                                  {it.payload?.workflow_id && <DetailRow label="Workflow ID" value={String(it.payload.workflow_id)} />}
                                  <div>
                                    <div className="font-medium text-foreground/70 mb-1">Raw payload sent</div>
                                    <pre className="bg-background border rounded p-2 overflow-x-auto text-[10px] leading-tight">{JSON.stringify(it.payload ?? {}, null, 2)}</pre>
                                  </div>
                                  {it.status === "failed" && it.error_message && (
                                    <div>
                                      <div className="font-medium text-red-600 mb-1">Error</div>
                                      <pre className="bg-red-500/5 border border-red-500/30 rounded p-2 overflow-x-auto text-[10px] leading-tight text-red-700">{it.error_message}</pre>
                                    </div>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                        </Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function labelFor(t: StatusFilter) {
  return { pending: "Pending pushes", processing: "Currently processing", succeeded: "Recently succeeded", failed: "Failed pushes", all: "All pushes" }[t];
}

function opLabel(op: string) {
  return { enroll_workflow: "Enroll in workflow", upsert_contact: "Sync contact", add_tag: "Add tag", remove_tag: "Remove tag", webhook_event: "Webhook event", send_message: "Send message" }[op as string] ?? op;
}

function timeAgo(iso: string | null) {
  if (!iso) return ",";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function Countdown({ target }: { target: string }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const diff = Math.floor((new Date(target).getTime() - now) / 1000);
  if (diff <= 0) {
    return (
      <span className="inline-flex items-center gap-1 text-blue-600 font-medium">
        <Loader2 className="h-3 w-3 animate-spin" /> firing now…
      </span>
    );
  }
  const h = Math.floor(diff / 3600);
  const m = Math.floor((diff % 3600) / 60);
  const s = diff % 60;
  const text = h > 0 ? `${h}h ${m}m ${s}s` : m > 0 ? `${m}m ${s}s` : `${s}s`;
  return (
    <span className="inline-flex items-center gap-1 text-amber-700 font-medium tabular-nums">
      <Timer className="h-3 w-3" /> fires in {text}
    </span>
  );
}

function StatCard({ icon, label, value }: { icon: React.ReactNode; label: string; value: number }) {
  return (
    <Card><CardContent className="pt-4">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">{icon}{label}</div>
      <div className="text-2xl font-semibold mt-1">{value}</div>
    </CardContent></Card>
  );
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    succeeded: "bg-green-500/15 text-green-700 border-green-500/40",
    failed: "bg-red-500/15 text-red-700 border-red-500/40",
    pending: "bg-amber-500/15 text-amber-700 border-amber-500/40",
    processing: "bg-blue-500/15 text-blue-700 border-blue-500/40",
    cancelled: "bg-muted text-muted-foreground",
    skipped: "bg-muted text-muted-foreground",
  };
  return <Badge variant="outline" className={`text-[10px] ${map[status] ?? ""}`}>{status}</Badge>;
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-2">
      <span className="font-medium text-foreground/70 min-w-[110px]">{label}:</span>
      <span className="text-muted-foreground break-all">{value}</span>
    </div>
  );
}

function stateLabel(s: string | null | undefined): string {
  if (!s) return "—";
  return s.replace(/_/g, " ");
}

// One-line "Data:" summary shown inline under every row
function payloadSummary(it: any): string {
  const p = it.payload ?? {};
  const email = it.contact?.email ?? p.email ?? "—";
  const parts: string[] = [`contact ${email}`];
  if (it.push_type === "state_transition" && p.to_state) {
    parts.push(`lifecycle ${stateLabel(p.from_state)} → ${stateLabel(p.to_state)}`);
  }
  if (p.tag) parts.push(`tag "${p.tag}"`);
  if (Array.isArray(p.tags) && p.tags.length) parts.push(`tags [${p.tags.join(", ")}]`);
  if (p.workflow_id) parts.push(`workflow ${p.workflow_id}`);
  if (it.push_type === "test") parts.push("synthetic test push");
  if (it.push_type === "manual") parts.push("manually triggered");
  return parts.join(" · ");
}

function triggerLabel(it: any): string {
  if (it.push_type === "test") return "Test button on integrations page";
  if (it.push_type === "manual") return "Manual push from contact / inbox";
  const p = it.payload ?? {};
  if (it.push_type === "state_transition" && p.to_state) {
    return `Lifecycle change: ${stateLabel(p.from_state)} → ${stateLabel(p.to_state)}`;
  }
  return "Automation rule";
}

function reasonLabel(it: any): string {
  const p = it.payload ?? {};
  if (it.push_type === "state_transition" && p.to_state) {
    return `Contact's segment moved to "${stateLabel(p.to_state)}" after the latest import reconciliation. The automation matching this lifecycle event ran ${opLabel(it.operation)}.`;
  }
  if (it.push_type === "test") {
    return "You clicked Test on this integration. A synthetic contact (saos-test@…) was upserted to verify the wire works end to end.";
  }
  if (it.push_type === "manual") {
    return "Triggered manually from a contact row or the anomaly inbox.";
  }
  return "—";
}

function SettleDelayCard() {
  const fetchList = useServerFn(listWorkspaceSettleDelays);
  const fetchQueue = useServerFn(listPushQueue);
  const updateFn = useServerFn(updateWorkspaceSettleDelay);
  const flushFn = useServerFn(flushWorkspacePushQueue);
  const qc = useQueryClient();
  const { data: workspaces, isLoading } = useQuery({
    queryKey: ["workspace-settle-delays"],
    queryFn: () => fetchList(),
  });
  const { data: pendingData } = useQuery({
    queryKey: ["push-queue", "pending"],
    queryFn: () => fetchQueue({ data: { status: "pending", limit: 500 } }),
    refetchInterval: 15_000,
  });

  // Map workspace_id -> { earliest next_attempt_at, count }
  const nextByWs = new Map<string, { next: string; count: number }>();
  for (const it of pendingData?.items ?? []) {
    if (!it.workspace_id || !it.next_attempt_at) continue;
    const cur = nextByWs.get(it.workspace_id);
    if (!cur || new Date(it.next_attempt_at).getTime() < new Date(cur.next).getTime()) {
      nextByWs.set(it.workspace_id, { next: it.next_attempt_at, count: (cur?.count ?? 0) + 1 });
    } else {
      cur.count += 1;
    }
  }
  const [drafts, setDrafts] = useState<Record<string, number>>({});

  const save = useMutation({
    mutationFn: (vars: { workspace_id: string; settle_minutes: number }) =>
      updateFn({ data: vars }),
    onSuccess: () => {
      toast.success("Settle delay updated");
      qc.invalidateQueries({ queryKey: ["workspace-settle-delays"] });
    },
    onError: (e: any) => toast.error(e.message),
  });

  const flush = useMutation({
    mutationFn: (workspace_id?: string) => flushFn({ data: { workspace_id } }),
    onSuccess: (res: any) => {
      toast.success(`Released ${res?.released ?? 0} pending push${res?.released === 1 ? "" : "es"}`);
      qc.invalidateQueries({ queryKey: ["push-queue"] });
    },
    onError: (e: any) => toast.error(e.message),
  });

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Settle delay</CardTitle>
        <p className="text-xs text-muted-foreground mt-1">
          After a report is imported, queued pushes wait this long before firing. The delay gives back-to-back uploads (e.g. trials then active users then commissions) time to fully reconcile so the CRM only receives the final, correct lifecycle state. Each new import for a workspace resets its pending pushes to a fresh window.
        </p>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="text-sm text-muted-foreground py-4">Loading workspaces…</div>
        ) : !workspaces?.length ? (
          <div className="text-sm text-muted-foreground py-4">No workspaces yet.</div>
        ) : (
          <div className="space-y-2">
            {workspaces.map((w: any) => {
              const current = drafts[w.id] ?? w.settle_minutes;
              const dirty = current !== w.settle_minutes;
              return (
                <div key={w.id} className="flex items-center gap-3 text-sm">
                  <div className="flex-1 min-w-0">
                    <div className="truncate">{w.program_name}</div>
                    {nextByWs.has(w.id) ? (
                      <div className="text-[11px] mt-0.5">
                        <Countdown target={nextByWs.get(w.id)!.next} />
                        <span className="text-muted-foreground ml-1.5">
                          · {nextByWs.get(w.id)!.count} pending
                        </span>
                      </div>
                    ) : (
                      <div className="text-[11px] text-muted-foreground mt-0.5">No pending pushes</div>
                    )}
                  </div>
                  <Input
                    type="number"
                    min={0}
                    max={240}
                    value={current}
                    onChange={(e) =>
                      setDrafts((p) => ({ ...p, [w.id]: Math.max(0, Math.min(240, Number(e.target.value) || 0)) }))
                    }
                    className="w-20 h-8"
                  />
                  <span className="text-xs text-muted-foreground w-12">minutes</span>
                  <Button
                    size="sm"
                    variant={dirty ? "default" : "outline"}
                    disabled={!dirty || save.isPending}
                    onClick={() => save.mutate({ workspace_id: w.id, settle_minutes: current })}
                  >
                    {dirty ? "Save" : "Saved"}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => flush.mutate(w.id)}
                    disabled={flush.isPending || !nextByWs.has(w.id)}
                    title="Release this workspace's pending pushes now"
                  >
                    <Play className="h-3 w-3 mr-1" /> Push now
                  </Button>
                </div>
              );
            })}
            <div className="pt-2 border-t mt-3 flex justify-end">
              <Button
                size="sm"
                variant="outline"
                onClick={() => flush.mutate(undefined)}
                disabled={flush.isPending}
              >
                <Play className="h-3 w-3 mr-1" /> Push all pending now
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
